/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { apiClient } from '@/lib/apiClient';

/** A manufacturing plant available in the settings "Plant" picker. */
export interface PlantDto {
  plantId: number;
  plantCode: string;
  plantName: string;
  plantTimeZone?: string;
  plantAbbr?: string;
  xrEnabled?: boolean;
  gsipEnabled?: boolean;
  eoltCountryPlantLine?: string;
}

/** A UI display language available in the settings "Language" picker. */
export interface LanguageDto {
  languageId: number;
  languageCode: string;
  displayString: string;
}

export type ThemeSetting = 'system' | 'light' | 'dark';
export type HomePreference = 'dealer' | 'manufacturer';

/** Enriched user settings from `GET /settings/user/{gmin}` (codes resolved to catalog entries). */
export interface UserSettingsModel {
  plant: PlantDto;
  languageDisplay: LanguageDto;
  userId: string;
  preferenceCode: HomePreference | string;
  themeSetting: ThemeSetting;
}

/** Flat user settings returned from the save endpoint. */
export interface UserSettingsDto {
  settingsId: number;
  userId: string;
  plantCode: string;
  languageCode: string;
  preferenceCode: string;
  themeSetting: ThemeSetting;
}

/** Per-user schematic-display preferences (`/api/v1/schematic-properties/user/{gmin}`). */
export interface SchematicProperties {
  userId: string;
  symbolSize: number;
  wireSize: number;
  spacing: number;
  showEngineeringLabels: boolean;
  showWireColors: boolean;
  showCommonViewTypes: boolean;
}

export async function getPlants(): Promise<PlantDto[]> {
  const { data } = await apiClient.get<PlantDto[]>('/ediagnostics/v1/settings/plants');
  return data ?? [];
}

export async function getLanguages(): Promise<LanguageDto[]> {
  const { data } = await apiClient.get<LanguageDto[]>('/ediagnostics/v1/settings/languages');
  return data ?? [];
}

export async function getUserSettings(gmin: string): Promise<UserSettingsModel> {
  const { data } = await apiClient.get<UserSettingsModel>(
    `/ediagnostics/v1/settings/user/${encodeURIComponent(gmin)}`,
  );
  return data;
}

/** Persists the user's app settings. All values go in the query string (matches the backend). */
export async function saveUserSettings(
  gmin: string,
  params: { plant: string; language: string; preference: string; theme?: ThemeSetting },
): Promise<UserSettingsDto> {
  const { data } = await apiClient.post<UserSettingsDto>(
    `/ediagnostics/v1/settings/user/${encodeURIComponent(gmin)}`,
    null,
    { params },
  );
  return data;
}

/**
 * The layout engine this caller starts on and the engines they may pick. Resolved server-side from
 * the audience flags, so the explorer's switcher cannot disagree with what the renderer does.
 */
export interface LayoutEngineOptions {
  layoutMode: string;
  available: string[];
}

export async function getLayoutEngineOptions(): Promise<LayoutEngineOptions> {
  const { data } = await apiClient.get<LayoutEngineOptions>('/ediagnostics/v1/user/layout-mode');
  return data;
}

export async function getSchematicProperties(gmin: string): Promise<SchematicProperties> {
  const { data } = await apiClient.get<SchematicProperties>(
    `/api/v1/schematic-properties/user/${encodeURIComponent(gmin)}`,
  );
  return data;
}

export async function saveSchematicProperties(
  gmin: string,
  properties: SchematicProperties,
): Promise<SchematicProperties> {
  const { data } = await apiClient.put<SchematicProperties>(
    `/api/v1/schematic-properties/user/${encodeURIComponent(gmin)}`,
    { ...properties, userId: gmin },
  );
  return data;
}
